import React, { useState, useEffect } from 'react';
import { DollarSign, Check, AlertCircle, ArrowLeft, CreditCard, Building2, Trophy } from 'lucide-react';
import { useAuth } from '../../context/UnifiedAuthContext.jsx';
import { useToastContext } from '../../contexts/ToastContext.jsx';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner.jsx';
import './RedeemRewards.scss';

const RedeemRewards = () => {
    const { technician, urlParams } = useAuth();
    const { showSuccess, showError, showInfo } = useToastContext();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [rewardsData, setRewardsData] = useState(null);
    const [redeeming, setRedeeming] = useState(false);

    useEffect(() => {
        fetchRewardsBalance();
    }, []);

    const fetchRewardsBalance = async () => {
        setLoading(true);
        try {
            const response = await fetch(`/api/payments/technicians/${technician.id}/rewards?location=${urlParams.location}&user=${urlParams.user}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            const result = await response.json();
            if (result.success) {
                setRewardsData(result.data);
            } else {
                showError('Failed to fetch rewards balance: ' + result.message);
            }
        } catch (error) {
            console.error('Error fetching rewards balance:', error);
            showError('Failed to fetch rewards balance. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRedeemRewards = async () => {
        setRedeeming(true);
        showInfo('Processing your reward redemption...');

        try {
            const response = await fetch(`/api/payments/technicians/${technician.id}/redeem-rewards?location=${urlParams.location}&user=${urlParams.user}`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json();
            
            if (result.success) {
                showSuccess(result.message);
                // Refresh the rewards balance
                await fetchRewardsBalance();
            } else {
                showError('Failed to redeem rewards: ' + result.message);
            }
        } catch (error) {
            console.error('Error redeeming rewards:', error);
            showError('Failed to redeem rewards. Please try again.');
        } finally {
            setRedeeming(false);
        }
    };

    const handleBack = () => {
        const params = new URLSearchParams();
        if (urlParams.location) params.set('location', urlParams.location);
        if (urlParams.user) params.set('user', urlParams.user);
        navigate('/dashboard?' + params.toString());
    };

    const handleSetupBankAccount = () => {
        const params = new URLSearchParams();
        if (urlParams.location) params.set('location', urlParams.location);
        if (urlParams.user) params.set('user', urlParams.user);
        navigate('/bank-account?' + params.toString());
    };

    const getAccountStatusText = (status) => {
        switch (status) {
            case 'active': return 'Bank Account Active';
            case 'pending': return 'Bank Account Pending Verification';
            case 'not_created': return 'Bank Account Not Set Up';
            case 'error': return 'Bank Account Error';
            default: return 'Unknown Status';
        }
    };

    const getAccountStatusColor = (status) => {
        switch (status) {
            case 'active': return '#10b981';
            case 'pending': return '#f59e0b';
            case 'not_created': return '#6b7280';
            case 'error': return '#ef4444';
            default: return '#6b7280';
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="redeem-rewards">
            <div className="redeem-rewards__header">
                <button className="back-button" onClick={handleBack}>
                    <ArrowLeft size={20} />
                    Back to Dashboard
                </button>
                
                <div className="redeem-rewards__title">
                    <DollarSign size={32} />
                    <div>
                        <h1>Redeem Rewards</h1>
                        <p>Cash out your accumulated rewards from positive customer reviews</p>
                    </div>
                </div>
            </div>

            <div className="redeem-rewards__content">
                {rewardsData && (
                    <>
                        {/* Rewards Balance Card */}
                        <div className="rewards-balance">
                            <div className="rewards-balance__header">
                                <Trophy size={24} />
                                <h2>Your Reward Balance</h2>
                            </div>
                            
                            <div className="rewards-balance__amount">
                                <span className="currency">$</span>
                                <span className="amount">{rewardsData.totalAmount.toFixed(2)}</span>
                            </div>
                            
                            <div className="rewards-balance__details">
                                <p>{rewardsData.availableRewardCount || rewardsData.rewardCount} unredeemed positive reviews × ${rewardsData.rewardAmountPerReview?.toFixed(2) || '5.00'} each</p>
                                {rewardsData.redeemedCount > 0 && (
                                    <p className="redeemed-notice">
                                        <Check size={16} />
                                        {rewardsData.redeemedCount} reviews already redeemed
                                    </p>
                                )}
                                {rewardsData.totalAmount < (rewardsData.rewardCount * 5) && (
                                    <p className="capped-notice">
                                        <AlertCircle size={16} />
                                        Capped at company maximum reward limit
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Account Status */}
                        <div className="account-status">
                            <div className="status-header">
                                <h3>Bank Account Status</h3>
                                <span 
                                    className="status-badge" 
                                    style={{ backgroundColor: getAccountStatusColor(rewardsData.accountStatus) }}
                                >
                                    {getAccountStatusText(rewardsData.accountStatus)}
                                </span>
                            </div>
                            
                            {rewardsData.accountStatus === 'not_created' && (
                                <div className="status-message">
                                    <AlertCircle size={16} />
                                    <span>You need to set up your bank account before you can redeem rewards.</span>
                                </div>
                            )}
                            
                            {rewardsData.accountStatus === 'pending' && (
                                <div className="status-message">
                                    <AlertCircle size={16} />
                                    <span>Your bank account is being verified by Stripe. This usually takes 1-2 business days.</span>
                                </div>
                            )}
                        </div>

                        {/* Cashout Requirements */}
                        <div className="cashout-requirements">
                            <h3>Cashout Requirements</h3>
                            <div className="requirements-list">
                                <div className={`requirement ${rewardsData.accountStatus === 'active' ? 'met' : 'not-met'}`}>
                                    <div className="requirement-icon">
                                        {rewardsData.accountStatus === 'active' ? <Check size={16} /> : <AlertCircle size={16} />}
                                    </div>
                                    <span>Bank account verified and active</span>
                                </div>
                                <div className={`requirement ${rewardsData.meetsThreshold ? 'met' : 'not-met'}`}>
                                    <div className="requirement-icon">
                                        {rewardsData.meetsThreshold ? <Check size={16} /> : <AlertCircle size={16} />}
                                    </div>
                                    <span>
                                        Minimum balance of ${(rewardsData.cashoutThreshold / 100).toFixed(2)} 
                                        (Current: ${rewardsData.totalAmount.toFixed(2)})
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="action-buttons">
                            {rewardsData.accountStatus === 'not_created' ? (
                                <button 
                                    className="btn-primary"
                                    onClick={handleSetupBankAccount}
                                >
                                    <Building2 size={16} />
                                    Set Up Bank Account
                                </button>
                            ) : rewardsData.canRedeem ? (
                                <button 
                                    className="btn-primary"
                                    onClick={handleRedeemRewards}
                                    disabled={redeeming}
                                >
                                    <CreditCard size={16} />
                                    {redeeming ? 'Processing...' : `Redeem $${rewardsData.totalAmount.toFixed(2)}`}
                                </button>
                            ) : (
                                <button 
                                    className="btn-disabled"
                                    disabled
                                >
                                    <CreditCard size={16} />
                                    Requirements Not Met
                                </button>
                            )}
                            
                            <button 
                                className="btn-secondary"
                                onClick={handleBack}
                            >
                                Back to Dashboard
                            </button>
                        </div>

                        {/* Info Notice */}
                        <div className="info-notice">
                            <AlertCircle size={16} />
                            <div>
                                <strong>How it works</strong>
                                <p>
                                    You earn ${rewardsData?.rewardAmountPerReview?.toFixed(2) || '5.00'} for each customer review of 4 stars or higher. 
                                    Once you meet the minimum cashout threshold and have a verified bank account, 
                                    you can redeem your rewards. Funds typically arrive in your bank account within 1-2 business days.
                                </p>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RedeemRewards;
